<!-- Component can only be used in microscope -->
<template>
  <div class="app-document-upload-wrapper" :class="{ disabled }">
    <div v-if="modelValue.length" class="file-list">
      <div v-for="(file, i) in modelValue" :key="`${i}-${file.name}`" class="file-row">
        <span class="file-name">{{ file.name }}</span>
        <AppButton
          icon
          color="transparent"
          :disabled="disabled"
          type="button"
          :aria-label="`Ta bort dokument: ${file.name}`"
          @click="removeFile(i)"
        >
          <SvgIcon :path="mdiCloseCircleOutline" :size="20" />
        </AppButton>
      </div>
    </div>

    <div
      class="drop-zone"
      :class="{ 'is-dragover': isDragover }"
      @drop.prevent="handleDrop"
      @dragover.prevent="handleDragover"
      @dragleave.prevent="handleDragleave"
    >
      <p class="drop-zone-text">Dra hit filer eller använd knappen nedan.</p>

      <div class="add-document-row">
        <input
          ref="fileInputRef"
          type="file"
          :accept="acceptAttribute"
          multiple
          class="file-input-hidden"
          tabindex="-1"
          @input="handleFileInput"
        />
        <AppButton type="button" :disabled="disabled" :loading="isValidating" small @click="openFilePicker">
          <span class="add-button-content">
            <SvgIcon :path="mdiPlusCircleOutline" :size="18" />
            <span>Lägg till dokument</span>
          </span>
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { mdiCloseCircleOutline, mdiPlusCircleOutline } from '@mdi/js'

import { detectFileType, acceptedMimeTypes, supportedFileTypes } from '../../utils/detectFileType'
import { notify } from '../../composables/notifications'
import AppButton from '../AppButton/AppButton.vue'
import SvgIcon from '../SvgIcon/SvgIcon.vue'

interface RejectedFile {
  filename: string
  reason: 'unnamed' | 'empty' | 'unsupported'
}

const supportedFileTypesText = `${[...supportedFileTypes].slice(0, -1).join(', ')} och ${supportedFileTypes[supportedFileTypes.length - 1]}`

const props = defineProps<{
  modelValue: File[]
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: File[]): void
}>()

const fileInputRef = ref<HTMLInputElement>()
const isDragover = ref(false)
const isValidating = ref(false)

const acceptAttribute = computed(() => acceptedMimeTypes.join(','))

const alreadyExists = (file: File) => {
  return props.modelValue.some((f) => f.name === file.name && f.size === file.size && f.type === file.type)
}

const notifyRejections = (rejected: RejectedFile[]) => {
  if (!rejected.length) return

  const unnamed = rejected.filter((r) => r.reason === 'unnamed')
  const empty = rejected.filter((r) => r.reason === 'empty')
  const unsupported = rejected.filter((r) => r.reason === 'unsupported')

  const messages: string[] = []

  if (unnamed.length) {
    messages.push(`${unnamed.length} ${unnamed.length === 1 ? 'fil' : 'filer'} saknade namn.`)
  }
  if (empty.length) {
    messages.push(`${empty.map((r) => r.filename).join(', ')} ${empty.length === 1 ? 'var tom' : 'var tomma'}.`)
  }
  if (unsupported.length) {
    messages.push(
      `${unsupported.map((r) => r.filename).join(', ')} har otillåten filtyp. Vi stödjer: ${supportedFileTypesText}.`,
    )
  }

  notify({
    type: 'error',
    title: 'Kunde inte lägga till dokument',
    description: messages.join(' '),
    dismissable: true,
  })
}

const validateAndAddFiles = async (files: FileList) => {
  if (isValidating.value) return

  isValidating.value = true

  try {
    const accepted: File[] = []
    const rejected: RejectedFile[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const { name, size } = file

      if (!name.trim()) {
        rejected.push({ filename: name || '(inget namn)', reason: 'unnamed' })
        continue
      }

      if (!size) {
        rejected.push({ filename: name, reason: 'empty' })
        continue
      }

      const detectedType = await detectFileType(file)
      if (!detectedType) {
        rejected.push({ filename: name, reason: 'unsupported' })
        continue
      }

      const acceptedFile = new File([file], name.trim(), {
        lastModified: file.lastModified,
        type: detectedType,
      })

      if (!alreadyExists(acceptedFile)) {
        accepted.push(acceptedFile)
      }
    }

    if (accepted.length) {
      emit('update:modelValue', [...props.modelValue, ...accepted])
    }

    notifyRejections(rejected)
  } finally {
    isValidating.value = false
  }
}

const openFilePicker = () => {
  fileInputRef.value?.click()
}

const handleFileInput = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files && input.files.length) {
    await validateAndAddFiles(input.files)
  }
  // Reset input so the same file can be selected again
  input.value = ''
}

const handleDrop = (event: DragEvent) => {
  isDragover.value = false
  const files = event.dataTransfer?.files
  if (files && files.length) {
    validateAndAddFiles(files)
  }
}

const handleDragover = () => {
  isDragover.value = true
}

const handleDragleave = () => {
  isDragover.value = false
}

const removeFile = (index: number) => {
  const updated = [...props.modelValue]
  updated.splice(index, 1)
  emit('update:modelValue', updated)
}
</script>

<style scoped lang="scss">
.app-document-upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.drop-zone {
  border: 2px dashed var(--color-border-default);
  background-color: var(--color-canvas-subtle);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &.is-dragover {
    border-color: var(--color-brand-subtle);
    background-color: var(--color-canvas-inset);
  }
}

.drop-zone-text {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--color-fg-default);
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-canvas-inset);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--color-border-muted);
}

.file-name {
  font-size: 14px;
  word-break: break-word;
  margin-right: 8px;
  color: var(--color-fg-default);
}

.add-document-row {
  display: flex;
  justify-content: center;
}

.add-button-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.file-input-hidden {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
